import { useEffect, useRef, useState } from 'react'
import RoutesMap from 'route_control/RouteControl'
import { Form } from 'react-bootstrap'
import { useFormik } from 'formik'
import { Text, Password, Email, Number as NumberInput, SelectBox, MultiSelectBox, SelectStatusBox, File, DatePicker } from 'components/input_fields'
import { AddPremiseUserSchema } from './ValidationSchema'
import { SaveButton } from 'components/buttons'
import Loader from 'components/loader/Loader'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { userRoleList } from '../../../../redux/slices/settings'
import { premiseList } from '../../../../redux/slices/premises'
import { addPremiseUser } from '../../../../redux/slices/users'
import { addUpdateHandler, SessionManager } from 'utils'
import { useTranslation } from 'react-i18next'

export default function AddPremiseUserForm() {

    const [loading, setLoading] = useState(true)
    const [spinner, setSpinner] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { state: locationState } = useLocation()
    const dispatch = useDispatch()
    const presetRoleApplied = useRef(false)
    const presetPremiseApplied = useRef(false)
    const role = useSelector(state => state?.userRole?.list)
    const premise = useSelector(state => state?.premise?.list)

    const role_option = role?.map(val => (
        { "value": val.id, "label": val.role_name }
    ))

    const premise_option = premise?.map(val => (
        { "value": val.id, "label": val.name }
    ))

    const gender_option = [
        { "value": 'Male', "label": 'Male' },
        { "value": 'Female', "label": 'Female' },
        { "value": 'Other', "label": 'Other' },
    ]

    const user_data = JSON.parse(SessionManager.getCookie("user"))

    const initialValues = {
        premise_id: user_data?.premise_id || '',
        role_id: [],
        is_staff: false,
        joining_date: '',
        salary: '',
        fname: '',
        lname: '',
        gender: '',
        username: '',
        password: '',
        confirmPassword: '',
        mobile: '',
        email: '',
        dob: '',
        profile: '',
        status: 'Active'
    }

    const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, setFieldTouched } = useFormik({
        initialValues: initialValues,
        validationSchema: AddPremiseUserSchema,
        enableReinitialize: true,

        onSubmit: (values, action) => {
            setSpinner(true)
            dispatch(addPremiseUser({ 'values': values })).then((res) => addUpdateHandler({
                res: res.payload,
                action,
                navigate,
                success_toast: `${t('premiseUser.premise_user')} ${t('common.success_add')}`,
                wrong_toast: `${t('common.wrong')}`,
                navigate_route: RoutesMap.PREMISE_USER_LIST.path,
                spinner: setSpinner,
            }))
        },
    })

    const handleRoleChange = (selectedRoles) => {
        setFieldValue('role_id', selectedRoles)
        const isStaff = selectedRoles?.some(roleId =>
            role?.find(r => r.id === roleId)?.is_staff
        ) || false
        setFieldValue('is_staff', isStaff)
        if (!isStaff) {
            setFieldValue('joining_date', '')
            setFieldValue('salary', '')
        }
    }

    useEffect(() => {
        setLoading(true)
        const pageable = { 'role_filter': 'below' }
        Promise.all([
            dispatch(userRoleList({ pageable, navigate })),
            dispatch(premiseList({ navigate })),
        ]).finally(() => setLoading(false));
    }, [])

    useEffect(() => {
        if (presetRoleApplied.current || loading || !role?.length) return

        const presetRoleId = locationState?.role_id
        if (presetRoleId == null || presetRoleId === '') return

        const roleId = +presetRoleId
        const matchedRole = role.find((item) => +item.id === roleId)
        if (!matchedRole) return

        presetRoleApplied.current = true
        setFieldValue('role_id', [roleId])
        setFieldValue('is_staff', Boolean(matchedRole?.is_staff))
        if (!matchedRole?.is_staff) {
            setFieldValue('joining_date', '')
            setFieldValue('salary', '')
        }
    }, [loading, role, locationState?.role_id, setFieldValue])

    useEffect(() => {
        if (presetPremiseApplied.current || loading || !premise?.length) return
        if (user_data?.premise_id) return

        const presetPremiseId = locationState?.premise_id
        if (presetPremiseId == null || presetPremiseId === '') return

        const premiseId = +presetPremiseId
        const matchedPremise = premise.find((item) => +item.id === premiseId)
        if (!matchedPremise) return

        presetPremiseApplied.current = true
        setFieldValue('premise_id', premiseId)
    }, [loading, premise, locationState?.premise_id, setFieldValue, user_data?.premise_id])

    return (
        <>
            <div className="container-fluid">
                <div className='layout'>
                    {loading ?
                        <div className="row">
                            <div className="col-md-12">
                                <Loader />
                            </div>
                        </div>
                        :
                        <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                            <div className="row">

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('premiseUser.premise')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={user_data?.premise_id}
                                        option={premise_option}
                                        name='premise_id'
                                        value={values.premise_id}
                                        onChange={(e) =>
                                            setFieldValue('premise_id', e)
                                        }
                                        onBlur={() => setFieldTouched("premise_id", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.premise_id && touched.premise_id ? (<span className='field_error'>{errors.premise_id}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <MultiSelectBox
                                        label_name={t('premiseUser.role')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        option={role_option}
                                        name='role_id'
                                        value={values.role_id}
                                        onChange={handleRoleChange}
                                        onBlur={() => setFieldTouched("role_id", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.role_id && touched.role_id ? (<span className='field_error'>{errors.role_id}</span>) : null}
                                    />
                                </div>

                                {values?.is_staff && (
                                    <>
                                        <div className="col-md-3">
                                            <DatePicker
                                                label_name={t('premiseUser.joining_date')}
                                                mandatory={true}
                                                placeholder=''
                                                name='joining_date'
                                                value={values.joining_date}
                                                onChange={(date, dateString) => setFieldValue('joining_date', dateString)}
                                                onBlur={() => setFieldTouched('joining_date', true)}
                                                error={errors.joining_date && touched.joining_date ? (<span className='field_error'>{errors.joining_date}</span>) : null}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <NumberInput
                                                label_name={t('premiseUser.salary')}
                                                mandatory={true}
                                                placeholder=''
                                                disabled={false}
                                                name='salary'
                                                value={values.salary}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                error={errors.salary && touched.salary ? (<span className='field_error'>{errors.salary}</span>) : null}
                                            />
                                        </div>
                                    </>
                                )}

                                <div className="col-md-3">
                                    <Text
                                        label_name={t('premiseUser.fname')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='fname'
                                        value={values.fname}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.fname && touched.fname ? (<span className='field_error'>{errors.fname}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Text
                                        label_name={t('premiseUser.lname')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='lname'
                                        value={values.lname}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.lname && touched.lname ? (<span className='field_error'>{errors.lname}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <SelectBox
                                        label_name={t('premiseUser.gender')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        option={gender_option}
                                        name='gender'
                                        value={values.gender}
                                        onChange={(e) =>
                                            setFieldValue('gender', e)
                                        }
                                        onBlur={() => setFieldTouched("gender", true)}
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        error={errors.gender && touched.gender ? (<span className='field_error'>{errors.gender}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Text
                                        label_name={t('premiseUser.username')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='username'
                                        value={values.username}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.username && touched.username ? (<span className='field_error'>{errors.username}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Password
                                        label_name={t('premiseUser.password')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='password'
                                        value={values.password}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        showPassword={showPassword}
                                        setShowPassword={setShowPassword}
                                        error={errors.password && touched.password ? (<span className='field_error'>{errors.password}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Password
                                        label_name={t('premiseUser.confirm_password')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='confirmPassword'
                                        value={values.confirmPassword}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        showPassword={showConfirmPassword}
                                        setShowPassword={setShowConfirmPassword}
                                        error={errors.confirmPassword && touched.confirmPassword ? (<span className='field_error'>{errors.confirmPassword}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <NumberInput
                                        label_name={t('premiseUser.mobile')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='mobile'
                                        value={values.mobile}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.mobile && touched.mobile ? (<span className='field_error'>{errors.mobile}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <Email
                                        label_name={t('premiseUser.email')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={false}
                                        name='email'
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={errors.email && touched.email ? (<span className='field_error'>{errors.email}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <DatePicker
                                        label_name={t('premiseUser.dob')}
                                        mandatory={false}
                                        placeholder=''
                                        name='dob'
                                        value={values.dob}
                                        onChange={(date, dateString) => setFieldValue('dob', dateString)}
                                        onBlur={() => setFieldTouched('dob', true)}
                                        error={errors.dob && touched.dob ? (<span className='field_error'>{errors.dob}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <File
                                        label_name={t('premiseUser.profile')}
                                        mandatory={false}
                                        disabled={false}
                                        name='profile'
                                        onChange={(e) =>
                                            setFieldValue('profile', e.currentTarget.files[0])
                                        }
                                        onBlur={handleBlur}
                                        error={errors.profile && touched.profile ? (<span className='field_error'>{errors.profile}</span>) : null}
                                        accept="image/jpg, image/jpeg, image/png"
                                    />
                                </div>

                                <div className="col-md-3">
                                    <SelectStatusBox
                                        label_name={t('premiseUser.status')}
                                        mandatory={true}
                                        placeholder=''
                                        disabled={true}
                                        name='status'
                                        value={values.status}
                                        onChange={(e) =>
                                            setFieldValue('status', e)
                                        }
                                        onBlur={() => setFieldTouched("status", true)}
                                        error={errors.status && touched.status ? (<span className='field_error'>{errors.status}</span>) : null}
                                    />
                                </div>

                                <div className="col-md-12">
                                    <div className="d-flex justify-content-end">
                                        <div className="col-lg-2">
                                            <SaveButton
                                                button_name={t('common.submit')}
                                                spinner={spinner}
                                            />
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </Form>
                    }
                </div>
            </div>
        </>
    )
}